import React, { useMemo, useState } from "react";
import { SimpleTableComponent } from "./simpleTable/SimpleTableComponent";
import { SimpleTableRow } from "./simpleTable/SimpleTableRow";
import { SimpleTableHeader } from "./simpleTable/SimpleTableHeader";
import { SimpleTableElement } from "./simpleTable/SimpleTableElement";
import {
  Comparable,
  ComparableForReview,
  ComparableForReviewStatus,
  ComparableFull,
  ComparableTransactionFull,
  MyProperty,
} from "common/types/common.types";
import { round } from "common/helpers/common.helpers";
import { getTranslatedFieldName } from "common/helpers/comparables.helpers";
import colors from "common/styles/colors";
import { addAlpha } from "common/helpers/colors.helpers";
import { ComparableCard } from "../map/ComparableCard";
import { Button } from "@mui/material";
import { useMessagesStatus } from "../messages/hooks";
import { TagComponent } from "../tags/TagComponent";
import { CenteredContentWrapper } from "../wrappers/CenteredContentWrapper";
import {
  ExpandLess,
  ExpandMore,
  UnfoldLess,
  UnfoldMore,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface SimilarityTableProps {
  comparables: (ComparableForReview | ComparableFull)[];
  property: MyProperty;
  showStatus?: boolean;
  enableGrouping?: boolean;
  readonly?: boolean;
}

export const SimilarityTable: React.FC<SimilarityTableProps> = ({
  comparables,
  property,
  showStatus = false,
  enableGrouping = false,
  readonly = false,
}) => {
  const { t } = useTranslation();
  const { data: messagesStatus } = useMessagesStatus();

  // Determine which fields we are showing based on the similarity details
  const fields = useMemo(() => {
    const fieldsSet = new Set<string>();
    comparables.forEach((c) => {
      c.similarities?.similarities_by_field?.forEach((f) => {
        fieldsSet.add(f.field);
      });
    });
    return Array.from(fieldsSet);
  }, [comparables]);

  const getFieldSimilarity = (
    comp: ComparableFull | Comparable | ComparableForReview,
    field: string
  ) => {
    return (
      comp.similarities?.similarities_by_field?.find((f) => f.field === field)
        ?.score ?? null
    );
  };

  const getStatusColor = (status: ComparableForReviewStatus) => {
    switch (status) {
      case ComparableForReviewStatus.Unchanged:
        return colors.green;
      case ComparableForReviewStatus.Removed:
        return colors.red;
      case ComparableForReviewStatus.Changed:
        return colors.orange;
      case ComparableForReviewStatus.Expanded:
        return colors.grey;
      default:
        return colors.grey;
    }
  };

  // Group comparables so that consecutive items with status Expanded are merged into a segment.
  // Only when grouping is enabled and we're dealing with ComparableForReview items
  const rows = useMemo(() => {
    if (!enableGrouping) {
      return comparables.map((comp) => ({
        type: "single" as const,
        data: comp,
      }));
    }

    const result: {
      type: "single" | "segment";
      data: ComparableForReview | ComparableForReview[];
    }[] = [];
    let i = 0;
    while (i < comparables.length) {
      const comp = comparables[i] as ComparableForReview;
      if (comp.status !== ComparableForReviewStatus.Expanded) {
        result.push({ type: "single", data: comp });
        i++;
      } else {
        const segment: ComparableForReview[] = [];
        while (
          i < comparables.length &&
          ((comparables[i] as ComparableForReview).status ===
            ComparableForReviewStatus.Expanded ||
            (comparables[i] as ComparableForReview).status ===
              ComparableForReviewStatus.Removed)
        ) {
          segment.push(comparables[i] as ComparableForReview);
          i++;
        }
        result.push({ type: "segment", data: segment });
      }
    }
    return result;
  }, [comparables, enableGrouping]);

  const segmentsIndexes = useMemo(() => {
    return rows.flatMap((r, index) => (r.type === "segment" ? [index] : []));
  }, [rows]);

  // State to track which segments (by index) are expanded.
  const [expandedSegments, setExpandedSegments] = useState<Set<number>>(
    new Set()
  );

  const toggleSegment = (segmentIndex: number) => {
    if (readonly) return;
    setExpandedSegments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(segmentIndex)) {
        newSet.delete(segmentIndex);
      } else {
        newSet.add(segmentIndex);
      }
      return newSet;
    });
  };

  // Calculate total number of columns in the table:
  // (1 for the property card, 1 for the joint similarity, one per field, and 1 for the status if shown)
  const totalColumns = fields.length + 2 + (showStatus ? 1 : 0);

  // Helper to render a single comparable row. Optionally indent the first cell (when inside a segment).
  const renderRow = (
    c: ComparableForReview | ComparableFull,
    isExpanded: boolean = false
  ) => {
    const ComparableReviewStatusTranslations: Record<
      ComparableForReviewStatus,
      string
    > = {
      [ComparableForReviewStatus.Changed]: t(
        "transparency.statusTypes.changed"
      ),
      [ComparableForReviewStatus.Removed]: t(
        "transparency.statusTypes.removed"
      ),
      [ComparableForReviewStatus.Unchanged]: t(
        "transparency.statusTypes.unchanged"
      ),
      [ComparableForReviewStatus.Expanded]: t(
        "transparency.statusTypes.expanded"
      ),
    };

    const comparable =
      "comparable_transaction" in c ? c.comparable_transaction : c;
    const status = messagesStatus?.[comparable.id];

    return (
      <SimpleTableRow
        key={comparable.id}
        style={{
          backgroundColor: isExpanded ? addAlpha("#A6A69E", 0.2) : undefined,
        }}
      >
        <SimpleTableElement isFirstElement>
          <ComparableCard
            comparable={c}
            isOverview
            noBorder
            status={status}
            property={property}
          />
        </SimpleTableElement>
        <SimpleTableElement style={{ textAlign: "center" }}>
          <b>{round(c.similarities?.joint_similarity ?? 0, 2).toFixed(2)}</b>
        </SimpleTableElement>
        {fields.map((f, index) => (
          <SimpleTableElement
            key={f}
            style={{
              paddingRight: index === fields.length - 1 ? 10 : undefined,
              textAlign: "center",
              width: "100px",
            }}
          >
            {round(getFieldSimilarity(c, f) ?? 0, 2).toFixed(2)}
          </SimpleTableElement>
        ))}
        {showStatus && (
          <SimpleTableElement>
            <TagComponent
              color={getStatusColor((c as ComparableForReview).status)}
            >
              <CenteredContentWrapper>
                {
                  ComparableReviewStatusTranslations[
                    (c as ComparableForReview).status
                  ]
                }
              </CenteredContentWrapper>
            </TagComponent>
          </SimpleTableElement>
        )}
      </SimpleTableRow>
    );
  };

  return (
    <>
      {enableGrouping && segmentsIndexes.length > 0 && !readonly && (
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {expandedSegments.size === segmentsIndexes.length ? (
            <Button
              onClick={() => setExpandedSegments(new Set())}
              startIcon={<UnfoldLess />}
            >
              {t("transparency.collapseAll")}
            </Button>
          ) : (
            <Button
              onClick={() => setExpandedSegments(new Set(segmentsIndexes))}
              startIcon={<UnfoldMore />}
            >
              {t("transparency.expandAll")}
            </Button>
          )}
        </div>
      )}

      <SimpleTableComponent>
        <SimpleTableRow>
          <SimpleTableHeader isFirstElement>
            {t("transparency.comparableObject")}
          </SimpleTableHeader>
          <SimpleTableHeader>{t("transparency.similarity")}</SimpleTableHeader>
          {fields.map((f) => (
            <SimpleTableHeader key={f}>
              {f === "distance"
                ? t("transparency.distance")
                : getTranslatedFieldName(
                    f as keyof ComparableTransactionFull,
                    t
                  )}
            </SimpleTableHeader>
          ))}
          {showStatus && (
            <SimpleTableHeader>{t("transparency.status")}</SimpleTableHeader>
          )}
        </SimpleTableRow>

        {rows.map((row, index) => {
          if (row.type === "single") {
            return renderRow(row.data as ComparableForReview | ComparableFull);
          } else {
            const segmentData = row.data as ComparableForReview[];
            const isExpanded = expandedSegments.has(index);
            return (
              <React.Fragment key={`segment-${index}`}>
                {/* Segment header row */}
                <SimpleTableRow
                  onClick={readonly ? undefined : () => toggleSegment(index)}
                  style={{
                    cursor: readonly ? "default" : "pointer",
                    background: addAlpha("#727272", 0.2),
                  }}
                >
                  <SimpleTableElement isFirstElement colSpan={totalColumns}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingLeft: "20px",
                        paddingRight: "20px",
                      }}
                    >
                      <div>
                        {t("transparency.nonSelectedObjects", {
                          count: segmentData.length,
                        })}
                      </div>
                      {!readonly && (
                        <div style={{ marginTop: "5px" }}>
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </div>
                      )}
                    </div>
                  </SimpleTableElement>
                </SimpleTableRow>
                {/* If expanded, render each row inside the segment with an indent */}
                {isExpanded && segmentData.map((comp) => renderRow(comp, true))}
              </React.Fragment>
            );
          }
        })}
      </SimpleTableComponent>
    </>
  );
};
